import { createSlice } from "@reduxjs/toolkit";
import {StreamFormat, StreamQuality} from "./StreamSlice";

export type UserRole = 'client'|'employee'|'administrator';
export type UserStatus = 'ACTIVE'|'BLOCKED';

export interface IUser {
	id: number,
	last_name: string,
	first_name: string,
	initials: string,
	status: UserStatus,
	email: string,
	email_verified_at: string|null,
	email_verification_code: string|null,
	password: string,
	created_by: number|null,
	updated_by: number|null,
	created_at: string|null,
	updated_at: string|null,
	stream_format_priority: StreamFormat,
	stream_quality_priority: StreamQuality,
	role: UserRole
}

export interface IUserInitialState {
	users: IUser[],
	user: IUser|null,
	search: IUser[],

	creating: boolean,
	deleting: boolean,
	updating: boolean,
	gettingList: boolean,
	gettingItem: boolean,
	searching: boolean,

	isError: boolean,
	errorCode: number,
	errorMessage: string|null,
}

export const initialState: IUserInitialState = {
	users: [],
	user: null,
	search: [],

	creating: false,
	deleting: false,
	updating: false,
	gettingList: false,
	gettingItem: false,
	searching: false,

	isError: false,
	errorCode: 0,
	errorMessage: null,
};

export const UserSlice = createSlice({
	name: 'user',
	initialState,
	reducers: {

		clearUserItem: (state, action): IUserInitialState => {
			return { ...state, user: null };
		},

		clearUserSearchResult: (state, action): IUserInitialState => {
			return { ...state, search: [] };
		},

		// Loading
		onLoadCreate: (state, action ): IUserInitialState => {
			return { ...state, creating: true };
		},

		onLoadDelete: (state, action ): IUserInitialState => {
			return { ...state, deleting: true };
		},

		onLoadUpdate: (state, action ): IUserInitialState => {
			return { ...state, updating: true };
		},

		onLoadList: (state, action ): IUserInitialState => {
			return { ...state, gettingList: true };
		},

		onLoadItem: (state, action ): IUserInitialState => {
			return { ...state, gettingItem: true };
		},


		onLoadSearch: (state, action ): IUserInitialState => {
			return { ...state, search: [], searching: true };
		},

		// Success
		onSuccessCreate: (state, action): IUserInitialState => {
			return {
				...state,
				users: [...state.users, action.payload.data],
				creating: false
			};
		},

		onSuccessDelete: (state, action): IUserInitialState => {
			return {
				...state,
				users: action.payload.data,
				deleting: false,
			};
		},

		onSuccessUpdate: (state, action): IUserInitialState => {
			const buffer = state.users.filter(e=>e.id !== action.payload.data.id);
			buffer.push(action.payload.data);
			return {
				...state,
				users: buffer,
				updating: false,
			};
		},

		onSuccessList: (state, action): IUserInitialState => {
			return {
				...state,
				users: action.payload.data.data,
				gettingList: false,
			};
		},

		onSuccessItem: (state, action): IUserInitialState => {
			return {
				...state,
				user: action.payload.data,
				gettingItem: false,
			};
		},

		onSuccessSearch: (state, action): IUserInitialState => {
			return {
				...state,
				search: action.payload.data,
				searching: false,
			};
		},

		// Errors
		onFailCreate: (state, action): IUserInitialState => {
			return { ...state, creating: false };
		},
		onFailDelete: (state, action): IUserInitialState => {
			return { ...state, deleting: false };
		},
		onFailUpdate: (state, action): IUserInitialState => {
			return { ...state, updating: false };
		},
		onFailList: (state, action): IUserInitialState => {
			return { ...state, users: [] };
		},
		onFailItem: (state, action): IUserInitialState => {
			return { ...state, user: null };
		},
		onFailSearch: (state, action): IUserInitialState => {
			return { ...state, search: [], searching: false };
		},
	},
});

export const {
	clearUserItem,
	clearUserSearchResult
} = UserSlice.actions;
